import { useState } from 'react';
import { Link } from 'react-router-dom';
import { KeyRound, Mail, LifeBuoy, LogOut, ArrowLeft, Camera } from 'lucide-react';
import { api, ApiError } from '../api';
import { useAuth } from '../auth';
import { PageHeader, Card, Button, Alert, Input, PasswordChecklist } from '../components/ui';
import { uploadStudentPhoto } from '../components/RequirePhoto';
import { WhatsAppHelp } from '../components/WhatsAppHelp';
import { passwordMeetsAllRules } from '@physioedge/shared';

const SUPPORT_EMAIL = 'support@physioedge.com';

export function StudentProfilePage() {
  const { me, logout, refetch } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwMsg, setPwMsg] = useState('');
  const [pwError, setPwError] = useState('');
  const [pwLoading, setPwLoading] = useState(false);

  const [resetMsg, setResetMsg] = useState('');
  const [resetError, setResetError] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [photoMsg, setPhotoMsg] = useState('');
  const [photoError, setPhotoError] = useState('');
  const [photoLoading, setPhotoLoading] = useState(false);

  const changePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setPwMsg('');
    setPwError('');
    if (!passwordMeetsAllRules(newPassword)) {
      setPwError('New password does not meet all requirements.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwError('New passwords do not match.');
      return;
    }
    if (newPassword === currentPassword) {
      setPwError('Choose a password that is different from your current one.');
      return;
    }
    setPwLoading(true);
    try {
      await api.post('/api/auth/change-password', { currentPassword, newPassword });
      setPwMsg('Password changed.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setPwError(err instanceof ApiError ? err.message : 'Could not change password.');
    } finally {
      setPwLoading(false);
    }
  };

  const requestResetEmail = async () => {
    if (!me?.email) return;
    setResetMsg('');
    setResetError('');
    setResetLoading(true);
    try {
      await api.post('/api/auth/password-reset/request', { email: me.email });
      setResetMsg('If that email is registered, a reset code has been sent. Check your inbox.');
    } catch (err) {
      setResetError(err instanceof ApiError ? err.message : 'Could not send email.');
    } finally {
      setResetLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    window.location.href = '/login';
  };

  return (
    <div className="min-h-screen bg-bg">
      <div className="max-w-lg mx-auto px-4 py-6 sm:py-8 space-y-6">
        <div>
          <Link to="/student" className="inline-flex items-center gap-1.5 text-sm text-ink-soft hover:text-ink mb-3">
            <ArrowLeft className="w-4 h-4" /> Club
          </Link>
          <PageHeader title="Profile" subtitle="Account settings and support" />
        </div>

        <Card className="p-5 space-y-2">
          <div className="flex items-center gap-4">
            {me?.photoUrl ? (
              <img src={me.photoUrl} alt="" className="w-16 h-16 rounded-full object-cover border border-line" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-teal-soft flex items-center justify-center text-teal font-bold text-xl">
                {(me?.fullName ?? '?').charAt(0)}
              </div>
            )}
            <div className="min-w-0">
              <div className="font-heading font-semibold text-ink text-lg">{me?.fullName}</div>
              <div className="text-sm text-ink-soft">{me?.email}</div>
              {me?.phone && <div className="text-sm text-ink-soft">{me.phone}</div>}
            </div>
          </div>
        </Card>

        <Card className="p-5">
          <div className="flex items-center gap-2 mb-2">
            <Camera className="w-5 h-5 text-teal" />
            <h2 className="font-heading font-semibold text-ink">Profile photo</h2>
          </div>
          <p className="text-sm text-ink-soft mb-3">
            Clinics use this to recognize you. Needed before you book a week or ask a mentor.
          </p>
          {photoMsg && <Alert type="success" className="mb-3">{photoMsg}</Alert>}
          {photoError && <Alert type="error" className="mb-3">{photoError}</Alert>}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="block w-full text-sm text-ink-soft mb-3"
            onChange={async e => {
              const file = e.target.files?.[0];
              if (!file) return;
              setPhotoMsg('');
              setPhotoError('');
              setPhotoLoading(true);
              try {
                await uploadStudentPhoto(file);
                await refetch();
                setPhotoMsg('Photo saved.');
              } catch (err) {
                setPhotoError(err instanceof ApiError ? err.friendlyMessage : 'Could not save photo.');
              } finally {
                setPhotoLoading(false);
                e.target.value = '';
              }
            }}
          />
          {photoLoading && <p className="text-xs text-ink-faint">Uploading…</p>}
        </Card>

        <Card className="p-5">
          <div className="flex items-center gap-2 mb-4">
            <KeyRound className="w-5 h-5 text-teal" />
            <h2 className="font-heading font-semibold text-ink">Change password</h2>
          </div>
          {pwMsg && <Alert type="success" className="mb-3">{pwMsg}</Alert>}
          {pwError && <Alert type="error" className="mb-3">{pwError}</Alert>}
          <form onSubmit={changePassword} className="space-y-3">
            <Input
              label="Current password"
              type="password"
              value={currentPassword}
              onChange={e => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
            <Input
              label="New password"
              type="password"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              autoComplete="new-password"
              required
            />
            <PasswordChecklist password={newPassword} currentPassword={currentPassword} />
            <Input
              label="Confirm new password"
              type="password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              required
            />
            <Button
              type="submit"
              loading={pwLoading}
              className="w-full"
              disabled={!passwordMeetsAllRules(newPassword) || newPassword !== confirmPassword || newPassword === currentPassword}
            >
              Save new password
            </Button>
          </form>
        </Card>

        <Card className="p-5">
          <div className="flex items-center gap-2 mb-2">
            <Mail className="w-5 h-5 text-teal" />
            <h2 className="font-heading font-semibold text-ink">Reset email</h2>
          </div>
          <p className="text-sm text-ink-soft mb-4">
            Send a password reset code to <span className="font-medium text-ink">{me?.email}</span>.
          </p>
          {resetMsg && <Alert type="success" className="mb-3">{resetMsg}</Alert>}
          {resetError && <Alert type="error" className="mb-3">{resetError}</Alert>}
          <Button variant="secondary" loading={resetLoading} onClick={requestResetEmail} className="w-full">
            Send reset code
          </Button>
          <Link to="/reset-password" className="block text-center text-sm text-sand-strong font-medium mt-3 hover:underline">
            I already have a code
          </Link>
        </Card>

        <Card className="p-5">
          <div className="flex items-center gap-2 mb-2">
            <LifeBuoy className="w-5 h-5 text-teal" />
            <h2 className="font-heading font-semibold text-ink">Contact support</h2>
          </div>
          <p className="text-sm text-ink-soft mb-4">
            Need help with your plan, bookings, or account? WhatsApp or email the PhysioEDGE team.
          </p>
          <div className="space-y-2">
            <WhatsAppHelp
              label="WhatsApp"
              className="w-full min-h-[48px] rounded-radius-sm bg-teal-soft px-4 no-underline hover:no-underline"
            />
            <a
              href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent('PhysioEDGE student support')}`}
              className="flex w-full items-center justify-center min-h-[48px] border border-line text-ink text-sm font-semibold rounded-radius-sm hover:bg-bg touch-manipulation"
            >
              Email {SUPPORT_EMAIL}
            </a>
          </div>
        </Card>

        <button
          type="button"
          onClick={handleLogout}
          className="w-full flex items-center justify-center gap-2 min-h-[48px] text-sm font-medium text-ink-soft hover:text-ink border border-line rounded-radius-sm touch-manipulation"
        >
          <LogOut className="w-4 h-4" /> Sign out
        </button>
      </div>
    </div>
  );
}
